import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Space, Tag, Modal, InputNumber, Descriptions, Typography, Select, Input } from 'antd';
import { MoreOutlined, FileTextOutlined, SendOutlined, DollarOutlined, CheckCircleOutlined, PrinterOutlined, CloseCircleOutlined, MailOutlined, SyncOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from '../dayjs';
import { DATE_STRING } from '../model/busRoute';
import EmailButton from './EmailButton';
import EmailModal from './EmailModal';
import { useAppContext } from '../libs/contextLib';
import { ReactComponent as Check } from '../assets/icons/Check.svg';
import { BOOKING_STATUS, QUOTE_STATUS } from '../model/charter';
import { values } from 'lodash';
import { formatCurrency } from '../libs/currency';

const { Text } = Typography;

const DocumentActionsMenu = ({ mode, charter, onAction, currentView, preview, mail, type = 'quote' }) => {
  const { operator } = useAppContext();
  const [showModal, setShowModal] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [amountPaidInput, setAmountPaidInput] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNote, setPaymentNote] = useState('');

  // Helper: get payment info
  const total = charter.calculateTotal();
  const payments = Array.isArray(charter.receiptDetails?.payments) ? charter.receiptDetails.payments : [];
  const paidSoFar = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const balance = total - paidSoFar;

  const handleEmail = () => {
    setShowModal(true);
    if (mail.onMailOpen) {
      mail.onMailOpen();
    }
  }

  const updateStatus = (quoteStatus, status = charter.status) => {
    onAction({ type: 'setQuoteStatus', payload: {
      quoteStatus,
      status
    } });

    // Set invoice details if creating an invoice
    if(quoteStatus === QUOTE_STATUS.invoiceDraft.value) {
      const currentDate = dayjs();
      onAction({ type: 'setInvoiceDetails', payload: {
        invoiceDate: currentDate,
        invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
        dueDate: currentDate.add(30, 'days'),
        paymentTerms: '7 days'
      } });
    }
  }

  const handleAction = (action) => {
    const currentDate = dayjs();
    const currentDateString = dayjs().format(DATE_STRING);
    // First let the charter model handle the action
    charter.handleDocumentAction({
      type: action, payload: {
        confirmationDetails: {
          confirmedDate: currentDateString,
          depositAmount: charter.calculateTotal() * 0.2, // 20% deposit by default
          depositDueDate: currentDate.add(7, 'days').format(DATE_STRING),
          confirmedBy: null,
          specialInstructions: null
        },
        invoiceDetails: {
          invoiceDate: dayjs(),
          invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
          dueDate: currentDate.add(30, 'days'),
          paymentTerms: '30 days',
          purchaseOrderNumber: null
        },
        receiptDetails: {
          receiptDate: currentDateString,
          receiptNumber: `RCP-${Date.now().toString().slice(-6)}`,
          paymentMethod: null,
          paymentReference: null,
          amountPaid: charter.calculateTotal()
        }
      }
    });

    // Then update the UI
    switch (action) {
      case 'unmarkAsSent':
        onAction({ type: 'setQuoteStatus', payload: {
          quoteStatus: 'draft',
          status: charter.status
        } });
        break;
      case 'markAsDeclined':
        onAction({ type: 'setQuoteStatus', payload: {
          quoteStatus: 'declined',
          status: charter.status
        } });
        break;
      case 'unmarkAsAccepted':
      case 'unmarkAsConfirmed':
      case 'unmarkAsDeclined':
        onAction({ type: 'setQuoteStatus', payload: {
          quoteStatus: 'sent',
          status: charter.status
        } });
        break;
      case 'voidInvoice':
        onAction({ type: 'setQuoteStatus', payload: {
          quoteStatus: 'invoice-draft',
          status: charter.status
        } });
        break;
      case 'email':
        handleEmail();
        break;
      case 'download':
        onAction({ type: 'download' });
        break;
      case 'print':
        onAction({ type: 'print' });
        break;
      case 'markAsFullyPaid': {
        // If not already fully paid, add a payment for the remaining balance
        const payments = Array.isArray(charter.receiptDetails?.payments) ? charter.receiptDetails.payments : [];
        const paidSoFar = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
        const balance = total - paidSoFar;
        if (balance > 0) {
          const newPayment = {
            amount: balance,
            date: dayjs().format('YYYY-MM-DD'),
            method: 'manual',
            reference: '',
            note: 'Marked as fully paid',
          };
          onAction({ type: 'setReceiptDetails', payload: { ...charter.receiptDetails, payments: [...payments, newPayment] } });
        }
        onAction({ type: 'setQuoteStatus', payload: {
          quoteStatus: QUOTE_STATUS.paid.value,
          status: charter.status
        } });
        break;
      }
      default:
        onAction({ type: action, payload: null });
        break;
    }
  };

  // Handler for Mark as Paid
  const handleMarkAsPaid = () => {
    setAmountPaidInput(balance > 0 ? balance : total);
    // Prefill with last payment's details if available
    const lastPayment = payments.length > 0 ? payments[payments.length - 1] : null;
    setPaymentMethod(lastPayment?.method || '');
    setShowMarkPaidModal(true);
  };

  // Confirm Mark as Paid
  const confirmMarkAsPaid = () => {
    const newAmount = Number(amountPaidInput) || 0;
    const newPayment = {
      amount: newAmount,
      date: dayjs().format('YYYY-MM-DD'),
      method: paymentMethod,
      reference: paymentReference,
      note: paymentNote,
    };
    const prevPayments = Array.isArray(charter.receiptDetails?.payments) ? charter.receiptDetails.payments : [];
    const updatedPayments = [...prevPayments, newPayment];
    const newPaidSoFar = updatedPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const newBalance = total - newPaidSoFar;
    onAction({ type: 'setReceiptDetails', payload: { ...charter.receiptDetails, payments: updatedPayments } });
    setShowMarkPaidModal(false);
    setPaymentMethod('');
    setPaymentReference('');
    setPaymentNote('');
    // Auto-mark as paid if fully paid
    if (newBalance <= 0) {
      onAction({ type: 'setQuoteStatus', payload: {
        quoteStatus: QUOTE_STATUS.paid.value,
        status: charter.status
      } });
    }
  };

  const getMenuItems = () => {
    const baseItems = [];

    switch (charter.quoteStatus) {
      case 'draft':
        baseItems.push(
          // { key: 'email', icon: <SendOutlined />, label: 'Send' },
          { key: 'download', icon: <DownloadOutlined />, label: 'Download' },
          { key: 'print', icon: <PrinterOutlined />, label: 'Print' }
        );
        break;
      case 'sent':
        baseItems.push(
          { key: 'markAsDeclined', icon: <CloseCircleOutlined />, label: 'Mark as Declined' },
          { key: 'unmarkAsSent', icon: <CloseCircleOutlined />, label: 'Unmark as Sent' },
          { type: 'divider' },
          { key: 'email', icon: <SendOutlined />, label: 'Send' },
          { key: 'download', icon: <DownloadOutlined />, label: 'Download' },
          { key: 'print', icon: <PrinterOutlined />, label: 'Print' }
        );
        break;
      case 'accepted':
        baseItems.push(
          { key: 'unmarkAsAccepted', icon: <CloseCircleOutlined />, label: 'Unmark as Accepted' },
          // { key: 'viewQuote', icon: <FileTextOutlined />, label: 'View Quote' },
          { type: 'divider' },
          { key: 'email', icon: <SendOutlined />, label: 'Send' },
          { key: 'download', icon: <DownloadOutlined />, label: 'Download' },
          { key: 'print', icon: <PrinterOutlined />, label: 'Print' },
        );
        break;
      case 'confirmed':
          baseItems.push(
            { key: 'unmarkAsConfirmed', icon: <CloseCircleOutlined />, label: 'Unmark as Confirmed' },
            // { key: 'viewQuote', icon: <FileTextOutlined />, label: 'View Quote' },
            { type: 'divider' },
            { key: 'email', icon: <SendOutlined />, label: 'Send' },
            { key: 'download', icon: <DownloadOutlined />, label: 'Download' },
            { key: 'print', icon: <PrinterOutlined />, label: 'Print' },
          );
          break;
      case 'declined':
        baseItems.push(
          { key: 'unmarkAsDeclined', icon: <CloseCircleOutlined />, label: 'Unmark as Declined' },
          // { key: 'viewQuote', icon: <FileTextOutlined />, label: 'View Quote' },
          { type: 'divider' },
          { key: 'email', icon: <SendOutlined />, label: 'Send' },
          { key: 'download', icon: <DownloadOutlined />, label: 'Download' },
          { key: 'print', icon: <PrinterOutlined />, label: 'Print' }
        );
        break;
      case 'invoice-draft':
        baseItems.push(
          // { key: 'viewQuote', icon: <FileTextOutlined />, label: 'View Quote' },
          // { key: 'viewConfirmation', icon: <CheckCircleOutlined />, label: 'View Quote Confirmation' },
          // { type: 'divider' },
          // { key: 'email', icon: <SendOutlined />, label: 'Send' },
          { key: 'download', icon: <DownloadOutlined />, label: 'Download' },
          { key: 'print', icon: <PrinterOutlined />, label: 'Print' }
        );
        break;
      case 'invoice-approved':
        baseItems.push(
          // { key: 'viewQuote', icon: <FileTextOutlined />, label: 'View Quote' },
          // { key: 'viewConfirmation', icon: <CheckCircleOutlined />, label: 'View Quote Confirmation' },
          // { type: 'divider' },
          { key: 'email', icon: <SendOutlined />, label: 'Send' },
          { key: 'download', icon: <DownloadOutlined />, label: 'Download' },
          { key: 'print', icon: <PrinterOutlined />, label: 'Print' }
        );
        break;
      case 'awaiting-payment':
        baseItems.push(
          { key: 'voidInvoice', icon: <CloseCircleOutlined />, label: 'Void Invoice' },
          { key: 'markAsFullyPaid', icon: <CheckCircleOutlined />, label: 'Mark as Fully Paid' },
          // { key: 'viewQuote', icon: <FileTextOutlined />, label: 'View Quote' },
          // { key: 'viewConfirmation', icon: <CheckCircleOutlined />, label: 'View Quote Confirmation' },
          { type: 'divider' },
          { key: 'email', icon: <SendOutlined />, label: 'Send' },
          { key: 'download', icon: <DownloadOutlined />, label: 'Download' },
          { key: 'print', icon: <PrinterOutlined />, label: 'Print' }
        );
        break;
      case 'paid':
        baseItems.push(
          // { key: 'viewQuote', icon: <FileTextOutlined />, label: 'View Quote' },
          // { key: 'viewConfirmation', icon: <CheckCircleOutlined />, label: 'View Quote Confirmation' },
          // { key: 'viewInvoice', icon: <DollarOutlined />, label: 'View Invoice' },
          // { type: 'divider' },
          // { key: 'email', icon: <SendOutlined />, label: 'Send' },
          { key: 'download', icon: <DownloadOutlined />, label: 'Download' },
          { key: 'print', icon: <PrinterOutlined />, label: 'Print' }
        );
    }

    return baseItems;
  };

  const actionItems = {
    items: getMenuItems(),
    onClick: ({ key }) => handleAction(key)
  };

  const getQuoteStatus = () => {
    const statuses = values(QUOTE_STATUS);
  
    // Map of the highest quote status before invoice statuses
    const highestQuoteStatus = 'accepted';
  
    // All quote and invoice statuses
    const quoteStatuses = ['draft', 'sent', 'accepted', 'declined'];
    const invoiceStatuses = ['invoice-draft', 'awaiting-payment', 'paid'];
  
    let displayStatusValue = charter.quoteStatus;
  
    if (type === 'quote') {
      // If the current status is an invoice status, show the highest quote status instead
      if (invoiceStatuses.includes(charter.quoteStatus)) {
        displayStatusValue = highestQuoteStatus;
      }
      // If the current status is not a quote status, fallback to the first quote status
      if (!quoteStatuses.includes(displayStatusValue)) {
        displayStatusValue = quoteStatuses[0];
      }
    } else if (type === 'invoice') {
      // If not an invoice status, fallback to the first invoice status
      if (!invoiceStatuses.includes(displayStatusValue)) {
        displayStatusValue = invoiceStatuses[0];
      }
    }
  
    const status = statuses.find(status => status.value === displayStatusValue) || statuses[0];
    return <Tag color={status.color}>{status.label}</Tag>;
  };

  const renderActionButton = () => {
    const status = charter.quoteStatus;

    // if (preview) {
    //   return <Button type="primary" className="icon-button" onClick={() => onAction({ type: 'cancelPreview' })}>
    //     Cancel Preview
    //   </Button>
    // }

    if (status === QUOTE_STATUS.draft.value) {
      return [
        <Button type="primary" className="icon-button icon-button-check" onClick={() => handleEmail()} icon={<MailOutlined />}>
          Send
        </Button>,
        <Button type="primary" className="icon-button icon-button-check" onClick={() => updateStatus(QUOTE_STATUS.sent.value)} icon={<Check />}>
          Mark as Sent
        </Button>
      ]
    }

    if (status === QUOTE_STATUS.sent.value) {
      return <Button type="primary" className="icon-button icon-button-check" onClick={() => updateStatus(QUOTE_STATUS.accepted.value, BOOKING_STATUS.accepted.value)} icon={<Check />}>
        Confirm Booking
      </Button>
    }

    if (status === QUOTE_STATUS.accepted.value) {
      return <Button type="primary" className="icon-button icon-button-check" onClick={() => updateStatus(QUOTE_STATUS.confirmed.value)} icon={<Check />}>
        Mark as Confirmed
      </Button>
    }

    if (status === QUOTE_STATUS.confirmed.value) {
      return <Button type="primary" className="icon-button" onClick={() => updateStatus(QUOTE_STATUS.invoiceDraft.value)} icon={<SyncOutlined />}>
        Create Invoice
      </Button>
    }

    if (status === QUOTE_STATUS.invoiceDraft.value) {
      return [
        <Button type="primary" className="icon-button icon-button-check" onClick={() => handleEmail()} icon={<MailOutlined />}>
          Send
        </Button>,
        <Button type="primary" className="icon-button icon-button-check" onClick={() => updateStatus(QUOTE_STATUS.awaitingPayment.value)} icon={<Check />}>
          Mark as Sent
        </Button>]
    }

    if (status === QUOTE_STATUS.awaitingPayment.value) {
      return <Button type="primary" className="icon-button icon-button-check" onClick={handleMarkAsPaid} icon={<DollarOutlined />}>
        Add Payment
      </Button>
    }

    if (status === QUOTE_STATUS.paid.value) {
      return <Button type="primary" className="icon-button icon-button-check" onClick={() => handleEmail()} icon={<MailOutlined />}>
        Send
      </Button>
    }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* <Space className='d-flex align-items-center' style={{ gap: 10 }}>
          <span>
            {currentView === 'quote' ? 'Quote' :
              currentView === 'confirmation' ? 'Booking Confirmation' :
                currentView === 'invoice' ? 'Invoice' : 'Receipt'}
          </span>
          <div className='simple-tags'>
            {getQuoteStatus()}
          </div>
        </Space> */}
        <div className='d-flex align-items-center' style={{ gap: 10 }}>
          {renderActionButton()}
          <Dropdown
            menu={actionItems}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} className="icon-button" />
          </Dropdown>
        </div>
      </div>
      <EmailModal mail={mail} showModal={showModal} setShowModal={setShowModal} />
      {/* Mark as Paid Modal */}
      <Modal
        open={showMarkPaidModal}
        title="Add Payment"
        onCancel={() => setShowMarkPaidModal(false)}
        onOk={confirmMarkAsPaid}
        okText="Confirm"
        destroyOnClose
      >
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Total Amount">{formatCurrency(total)}</Descriptions.Item>
          <Descriptions.Item label="Amount Paid So Far">{formatCurrency(paidSoFar)}</Descriptions.Item>
          <Descriptions.Item label="Remaining Balance">
            <Text type={balance > 0 ? 'danger' : 'success'}>{formatCurrency(balance)}</Text>
          </Descriptions.Item>
        </Descriptions>
        <div style={{ marginTop: 20 }}>
          <label>Enter Amount Paid</label>
          <InputNumber
            className="w-100"
            min={0}
            max={balance}
            value={amountPaidInput}
            onChange={setAmountPaidInput}
            formatter={value => formatCurrency(value)}
            parser={value => value.replace(/[^\d.-]/g, '')}
          />
        </div>
        <div style={{ marginTop: 10 }}>
          <label>Payment Method</label>
          <Select
            className="w-100"
            value={paymentMethod}
            onChange={setPaymentMethod}
            options={[
              { value: 'credit_card', label: 'Credit Card' },
              { value: 'bank_transfer', label: 'Bank Transfer' },
              { value: 'cash', label: 'Cash' },
              { value: 'cheque', label: 'Cheque' },
            ]}
          />
        </div>
        <div style={{ marginTop: 10 }}>
          <label>Payment Reference</label>
          <Input
            className="w-100"
            value={paymentReference}
            onChange={e => setPaymentReference(e.target.value)}
          />
        </div>
        <div style={{ marginTop: 10 }}>
          <label>Note (optional)</label>
          <Input
            className="w-100"
            value={paymentNote}
            onChange={e => setPaymentNote(e.target.value)}
          />
        </div>
      </Modal>
    </>
  );
};

export default DocumentActionsMenu; 